import React, { useState, useEffect } from 'react';
import { spacing, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { safeEvaluatePattern } from '../../utils/safePatternEvaluator';

const ASCII_CHARS = ' .:-=+*#%@';

/**
 * Generate a small ASCII preview from a pattern function
 */
const generateThumbnailASCII = (patternFunc, width, height) => {
  let result = '';
  const t = 0; // Static preview at t=0
  const density = 0.8;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      try {
        const value = patternFunc(x, y, t, density);
        const normalized = Math.max(0, Math.min(1, (value + 1) / 2));
        const charIndex = Math.floor(normalized * (ASCII_CHARS.length - 1));
        result += ASCII_CHARS[charIndex];
      } catch {
        result += ' ';
      }
    }
    result += '\n';
  }
  return result;
};

/**
 * PatternThumbnail Component
 *
 * Displays a clickable mini ASCII preview of a generated pattern.
 * Used in chat messages to show pattern results.
 */
const PatternThumbnail = ({ patternCode, isSelected, onClick }) => {
  const { colors } = useTheme();
  const [preview, setPreview] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    try {
      const patternFunc = safeEvaluatePattern(patternCode);
      const ascii = generateThumbnailASCII(patternFunc, 24, 10);
      setPreview(ascii);
    } catch (err) {
      setPreview('Preview unavailable');
    }
  }, [patternCode]);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'block',
        width: '100%',
        marginTop: spacing.sm,
        padding: spacing.md,
        background: isSelected ? colors.backgroundOffset : colors.backgroundPanel,
        border: `2px solid ${isSelected ? '#6D916C' : isHovered ? colors.foregroundTertiary : colors.border}`,
        borderRadius: borderRadius.md,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'left',
      }}
    >
      <pre style={{
        fontFamily: 'SF Mono, Monaco, Consolas, monospace',
        fontSize: '0.45rem',
        lineHeight: 1.1,
        margin: 0,
        color: colors.foreground,
        letterSpacing: '0.02em',
        overflow: 'hidden',
      }}>
        {preview}
      </pre>
      <div style={{
        fontSize: '0.6875rem',
        marginTop: spacing.xs,
        color: isSelected ? '#6D916C' : colors.foregroundSecondary,
        fontWeight: isSelected ? 500 : 400,
      }}>
        {isSelected ? 'Currently viewing' : 'Click to view'}
      </div>
    </button>
  );
};

export default PatternThumbnail;
